
import React from "react";
import { Profile } from "@/services/profiles/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ExternalLink, MessageSquare, Linkedin, Globe } from "lucide-react";
import { formatDate } from "@/utils/dateUtils";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";

interface PublicProfileSectionProps {
  profile: Profile;
}

const PublicProfileSection: React.FC<PublicProfileSectionProps> = ({ profile }) => {
  const navigate = useNavigate();
  
  const displayName = profile.display_username || profile.username || "Anonymous User";
  
  // Generate initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  
  // Helper function to ensure URLs have proper format
  const formatUrl = (url: string | null): string | null => {
    if (!url) return null;
    
    // Return if already has protocol
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Add https:// prefix
    return `https://${url}`;
  };
  
  const startConversation = () => {
    // Navigate to the conversation page with this user's ID
    navigate(`/conversations/${profile.id}`);
  };
  
  const joinDate = profile.created_at ? formatDate(profile.created_at) : "Unknown";

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle>Profile Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-start gap-4">
          <Avatar className="h-16 w-16">
            <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
          </Avatar>
          
          <div className="space-y-1 flex-grow">
            <h2 className="text-2xl font-bold">{displayName}</h2>
            <p className="text-muted-foreground">Member since {joinDate}</p>
            
            <div className="flex flex-wrap space-x-2 mt-3">
              <Button onClick={startConversation} size="sm">
                <MessageSquare className="h-4 w-4 mr-2" />
                Message
              </Button>
              
              {profile.linkedin_url && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  asChild
                >
                  <a href={formatUrl(profile.linkedin_url)} target="_blank" rel="noopener noreferrer">
                    <Linkedin className="h-4 w-4 mr-2" />
                    LinkedIn
                  </a>
                </Button>
              )}
              
              {profile.website_url && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  asChild
                >
                  <a href={formatUrl(profile.website_url)} target="_blank" rel="noopener noreferrer">
                    <Globe className="h-4 w-4 mr-2" />
                    Website
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {profile.university && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-1">University/Institution</h3>
              <p>{profile.university}</p>
            </div>
          )}
          
          {profile.lab && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Lab</h3>
              <p>{profile.lab}</p>
            </div>
          )}
          
          {(profile.city || profile.country) && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Location</h3>
              <p>{[profile.city, profile.country].filter(Boolean).join(", ")}</p>
            </div>
          )}
          
          {profile.expertise && (
            <div className="md:col-span-2">
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Expertise</h3>
              <div className="flex flex-wrap gap-2">
                {profile.expertise.split(',').map(item => (
                  <Badge key={item.trim()} variant="secondary">
                    {item.trim()}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {profile.research_interests && (
            <div className="md:col-span-2">
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Research Interests</h3>
              <p>{profile.research_interests}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PublicProfileSection;
